/**
 * The notification bell: GET /api/notifications polled once a minute while the app is visible (never in
 * the background), passing back the last answer's stamp so the server can answer "unchanged" cheaply.
 * Persisted, so the bell and the app badge show the last known state offline and right after a start.
 *
 * Query key ['notifications', userId, role, section]: a new role or voice starts from a full answer.
 * Reading a thread (event board, chat) drops its item here at once; the next poll confirms it.
 */
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { MeDTO } from '../../shared/dto/auth';
import type { MarkNotificationsReadRequest, NotificationItemDTO, NotificationsDTO } from '../../shared/dto/chat';
import { notificationTotal } from '../../shared/notifications';
import { api } from './client';
import { visibleInterval } from './poll';

export interface BellState {
  items: NotificationItemDTO[];
  total: number;
  /** Stamp of the last full answer (server time). */
  stamp: number;
}

export const NOTIFY_POLL_MS = 60_000;

export const notificationsKey = (me: Pick<MeDTO, 'id' | 'role' | 'section'>) => ['notifications', me.id, me.role, me.section ?? ''] as const;

async function fetchBell(qc: QueryClient, key: readonly unknown[]): Promise<BellState> {
  const prev = qc.getQueryData<BellState>(key);
  const res = await api<NotificationsDTO>(`/notifications${prev ? `?since=${prev.stamp}` : ''}`);
  if (!res.unchanged) return { items: res.items, total: res.total, stamp: res.stamp };
  // Re-read: a thread may have been read meanwhile (dropNotification).
  const cur = qc.getQueryData<BellState>(key) ?? prev;
  if (cur) return { ...cur, stamp: res.stamp };
  const full = await api<NotificationsDTO>('/notifications');
  return full.unchanged ? { items: [], total: 0, stamp: full.stamp } : { items: full.items, total: full.total, stamp: full.stamp };
}

/**
 * The bell's data for the signed-in member `me` (useMe()); polls while visible — one query however many
 * bells are mounted. (No React context import here: api/ modules stay free of .tsx for the tests.)
 */
export function useNotifications(me: Pick<MeDTO, 'id' | 'role' | 'section'> | null) {
  const qc = useQueryClient();
  const key = me ? notificationsKey(me) : (['notifications', ''] as const);
  return useQuery({
    queryKey: key,
    queryFn: () => fetchBell(qc, key),
    enabled: !!me,
    meta: { persist: true },
    staleTime: 50_000,
    refetchInterval: visibleInterval(NOTIFY_POLL_MS),
    refetchIntervalInBackground: false,
  });
}

function patchBells(qc: QueryClient, fn: (items: NotificationItemDTO[]) => NotificationItemDTO[]): void {
  for (const q of qc.getQueryCache().findAll({ queryKey: ['notifications'] })) {
    const cur = q.state.data as BellState | undefined;
    if (!cur) continue;
    const items = fn(cur.items);
    if (items.length !== cur.items.length || items.some((it, i) => it !== cur.items[i])) qc.setQueryData<BellState>(q.queryKey, { ...cur, items, total: notificationTotal(items) });
  }
}

/** A thread was read on this device: its item leaves the bell (unless something newer than `upTo` came in). */
export function dropNotification(qc: QueryClient, kind: NotificationItemDTO['kind'], id: string, upTo?: number): void {
  patchBells(qc, (items) => items.filter((it) => !(it.kind === kind && it.id === id && (upTo === undefined || it.latestAt <= upTo))));
}

/** "Merkja allt lesið": every listed thread up to the newest message the bell showed. */
export async function markAllNotificationsRead(qc: QueryClient, items: readonly NotificationItemDTO[]): Promise<void> {
  const body: MarkNotificationsReadRequest = {
    events: items.filter((it) => it.kind === 'event').map((it) => ({ id: it.id, upTo: it.latestAt })),
    chats: items.filter((it) => it.kind === 'chat').map((it) => ({ id: it.id as MarkNotificationsReadRequest['chats'][number]['id'], upTo: it.latestAt })),
  };
  await api<void>('/notifications/read', { method: 'POST', body });
  const done = new Set(items.map((it) => `${it.kind}:${it.id}:${it.latestAt}`));
  patchBells(qc, (list) => list.filter((it) => !done.has(`${it.kind}:${it.id}:${it.latestAt}`)));
  void qc.invalidateQueries({ queryKey: ['chats'] });
  void qc.invalidateQueries({ queryKey: ['events'] });
  void qc.invalidateQueries({ queryKey: ['event'] });
}

type BadgeNavigator = Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };

/** The app icon's badge (Home-Screen apps; iOS 16.4+): unread messages in the bell. */
export function useAppBadge(total: number | undefined): void {
  useEffect(() => {
    if (total === undefined) return;
    const nav = navigator as BadgeNavigator;
    const p = total > 0 ? nav.setAppBadge?.(total) : nav.clearAppBadge?.();
    p?.catch(() => undefined);
  }, [total]);
}
